import { useState } from 'react';
import '../css/aiSearchLand.css';
import { mapUtil } from '../util/mapUtil.js';
import { useT, currentLang, displayAddress } from '../i18n';

export default function AiSearchLand({ aiSearchLand, setAiSearchLand }) {
    const t = useT();

    // 억 단위는 영문권에 통하지 않아 환산해 보여 준다. (3억 → ₩300M, 100억 → ₩10B)
    const formatEok = (n) => {
        if (currentLang !== 'en') return `${n}억`;
        return n >= 10
            ? `\u20a9${(n / 10).toLocaleString('en-US', { maximumFractionDigits: 1 })}B`
            : `\u20a9${n * 100}M`;
    };
    const [selectedRegions, setSelectedRegions] = useState(['서울']);
    const [budgetRange, setBudgetRange] = useState([10, 100]); // 억 단위
    const [constructionDate, setConstructionDate] = useState('2025.08');
    const [description, setDescription] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // regions 값은 검색 요청에 그대로 실려 가므로 한국어를 유지한다(표시만 번역).
    const regions = [
        '서울', '부산', '대구', '인천', '광주', '대전', '울산', '세종',
        '경기도', '강원도', '충청북도', '충청남도', '전라북도', '전라남도', '경상북도', '경상남도', '제주도'
    ];
    const budgetValues = [3, 10, 20, 50, 100]; // 슬라이더 값 0~4에 대응하는 실제 예산

    // 슬라이더 값(0~4)을 예산 값으로 변환
    const sliderToValue = (index) => budgetValues[index];

    // 예산 값을 슬라이더 값(0~4)으로 변환
    const valueToSlider = (value) => {
        for (let i = 0; i < budgetValues.length; i++) {
            if (budgetValues[i] >= value) return i;
        }
        return budgetValues.length - 1;
    };



    const handleSearch = async () => {
        console.log('검색 조건:', {
            regions: selectedRegions,
            budgetRange,
            constructionDate,
            description
        });

        setIsLoading(true);
        setShowResults(false);

        try {
            // 로딩 상태 표시
            window.setModalMessage(t('AI가 조건에 맞는 토지를 찾고 있습니다...'));
            
            const requestData = {
                regions: selectedRegions,
                budgetRange,
                constructionDate,
                description: description || '조건에 맞는 토지를 찾아주세요',
                lang: currentLang, // 영문 화면이면 추천 이유를 영문으로 받는다
            };
            
            console.log('요청 데이터:', requestData);
            
            const response = await fetch(`${process.env.REACT_APP_BACKEND_SERVER}/api/ai-land-search`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestData)
            });

            if (!response.ok) {
                throw new Error(`서버 오류: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.status === 'success') {
                setSearchResults(data.data);
                setExpandedId(data.data[0]?.id ?? null);
                setShowResults(true);
                window.setModalMessage(t('AI 검색이 완료되었습니다!'));
            } else {
                throw new Error(data.message || '검색 중 오류가 발생했습니다.');
            }
            
        } catch (error) {
            console.error('AI 검색 오류:', error);
            window.setModalMessage(t('검색 중 오류가 발생했습니다. 다시 시도해주세요.'));
            
            // 오류 발생 시 기본 결과 표시
            const fallbackResults = [
                {
                    id: 1,
                    region: selectedRegions[0],
                    address: t('{region} 지역의 적합한 토지', { region: t(selectedRegions[0]) }),
                    reason: t("현재 AI 서비스에 일시적인 문제가 발생했습니다. 부동산 전문가와 상담하시기 바랍니다.")
                }
            ];
            setSearchResults(fallbackResults);
            setExpandedId(fallbackResults[0]?.id ?? null);
            setShowResults(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleReset = () => {
        setShowResults(false);
        setSearchResults([]);
        setExpandedId(null);
        setIsLoading(false);
    };

    return (
        <div className={`ai_search_land_overlay ${aiSearchLand ? 'show' : ''}`}>
            <div className="ai_search_land_modal">
                <div className="ai_search_land_header">
                    <div className="ai_search_land_title_section">
                        <p className="ai_search_land_subtitle">{t('원하는 토지/건축을 찾기 힘드신가요?')}</p>
                        <h1 className="ai_search_land_title">
                            {t('AI 토지/건축물 찾기')}
                            <span className="ai_search_land_icon"><img src={process.env.PUBLIC_URL + 'images/ai_search_icon.png'} alt={t('AI 토지/건축물 찾기')} /></span>
                        </h1>
                    </div>
                    <button
                        className="ai_search_land_close"
                        onClick={() => setAiSearchLand(false)}
                    >
                    </button>
                </div>
                <div className="row"></div>

                <div className="ai_search_land_content">
                    {!showResults ? (
                        <>
                            {/* 지역 선택 섹션 */}
                            <div className="ai_search_land_section">
                                <h3 className="ai_search_land_section_title">
                                    {t('원하는 지역을 선택해주세요.')} <span className="ai_search_land_section_title_hint">{t('(최대 3개까지 선택 가능)')}</span>
                                </h3>
                                <div className="ai_search_land_regions">
                                    <div className="ai_search_land_dropdown_container">
                                        <div
                                            className="ai_search_land_dropdown_trigger"
                                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                        >
                                            <div className="ai_search_land_dropdown_text_wrap">
                                                {selectedRegions.length > 0 ? selectedRegions.map(region => {
                                                    return (
                                                        <div key={region} className="ai_search_land_dropdown_item_text_border">
                                                            <span key={region} className="ai_search_land_dropdown_item_text">{t(region)}</span>

                                                        </div>
                                                    )
                                                }) : <span className="ai_search_land_dropdown_item_text empty">{t('지역을 선택해주세요')}</span>}
                                            </div>
                                            <span className={`ai_search_land_dropdown_arrow ${isDropdownOpen ? 'open' : ''}`}>
                                                <img src={process.env.PUBLIC_URL + 'images/icon_arrow_down.png'} alt={t('AI 토지/건축물 지역 선택 아이콘')} />
                                            </span>
                                        </div>

                                        {isDropdownOpen && (
                                            <div className="ai_search_land_dropdown_menu">
                                                {regions.map((region, index) => (
                                                    <label key={index} className="ai_search_land_dropdown_item">
                                                        <input
                                                            type="checkbox"
                                                            value={region}
                                                            checked={selectedRegions.includes(region)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    if (selectedRegions.length < 3) {
                                                                        setSelectedRegions([...selectedRegions, region]);
                                                                    }
                                                                } else {
                                                                    setSelectedRegions(selectedRegions.filter(r => r !== region));
                                                                }
                                                            }}
                                                        />
                                                        <span className="ai_search_land_dropdown_item_menu_text">{t(region)}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="row"></div>

                            {/* 예산 섹션 */}
                            <div className="ai_search_land_section">
                                <div className="select_title">
                                    {t('예산안을 알려주세요.')}
                                </div>
                                <div className="consulting_budget">
                                    <div className="budget">
                                        {budgetRange[0] === budgetRange[1] ? formatEok(budgetRange[0]) :
                                            budgetRange[0] > budgetRange[1] ? `${formatEok(budgetRange[1])} ~ ${formatEok(budgetRange[0])}` :
                                                `${formatEok(budgetRange[0])} ~ ${formatEok(budgetRange[1])}`
                                        }
                                    </div>
                                    <div className="row_input">
                                        <div className="cube"><div className='circle_gray'></div></div>
                                        <div className="cube"><div className='circle_gray'></div></div>
                                        <div className="cube"><div className='circle_gray'></div></div>
                                        <div className="cube"><div className='circle_gray'></div></div>
                                        <div className="cube"><div className='circle_gray'></div></div>
                                        <div
                                            className="row_dark"
                                            style={{
                                                left: budgetRange[0] === budgetRange[1]
                                                    ? (25 * valueToSlider(budgetRange[0])) + '%'
                                                    : (25 * Math.min(valueToSlider(budgetRange[0]), valueToSlider(budgetRange[1]))) + "%",
                                                width: budgetRange[0] >= budgetRange[1]
                                                    ? (25 * (valueToSlider(budgetRange[0]) - valueToSlider(budgetRange[1])) + "%")
                                                    : (25 * (valueToSlider(budgetRange[1]) - valueToSlider(budgetRange[0])) + "%")
                                            }}
                                        ></div>

                                        <div className="cube_purple" style={{ left: (25 * valueToSlider(budgetRange[0])) + '%' }}><div className="circle_blue"></div></div>
                                        <input
                                            className="range"
                                            name="range-a"
                                            id="range"
                                            type="range"
                                            min="0"
                                            max="4"
                                            value={valueToSlider(budgetRange[0])}
                                            onInput={(e) => {
                                                let newVal = parseInt(e.target.value, 10);
                                                const maxVal = valueToSlider(budgetRange[1]);

                                                if (newVal >= maxVal) {
                                                    newVal = maxVal - 1;
                                                    if (newVal < 0) newVal = 0;
                                                }

                                                setBudgetRange([sliderToValue(newVal), budgetRange[1]]);
                                            }}
                                        />

                                        <input
                                            className="range"
                                            name="range-b"
                                            id="range-1b"
                                            type="range"
                                            min="0"
                                            max="4"
                                            value={valueToSlider(budgetRange[1])}
                                            onInput={(e) => {
                                                let newVal = parseInt(e.target.value, 10);
                                                const minVal = valueToSlider(budgetRange[0]);

                                                if (newVal <= minVal) {
                                                    newVal = minVal + 1;
                                                    if (newVal > 4) newVal = 4;
                                                }

                                                setBudgetRange([budgetRange[0], sliderToValue(newVal)]);
                                            }}
                                        />
                                        <div className="cube_purple" style={{ left: (25 * valueToSlider(budgetRange[1])) + '%' }}><div className="circle_purple"></div></div>
                                    </div>
                                    <div className="budget_text">
                                        <div className="left">{formatEok(3)}</div>
                                        <div className="right">{formatEok(100)}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="row"></div>

                            {/* 건축 예정일 섹션 */}
                            <div className="content_wrap ai_simulation">
                                <div className="content_box">
                                    <div className="title">{t('건축 예정일')}</div>
                                    <input type="text" value={constructionDate} onChange={(e) => setConstructionDate(e.target.value)} className="content" />
                                </div>
                                <div className="row"></div>
                                <div className="content_box">
                                    <div className="title">{t('원하는 토지/건축물에 대해 설명해주세요.')}</div>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        className="content"
                                        placeholder={t("ex) 서울, 상업시설, 200평 이상, 10억~30억의 예산안\n'단독주택을 짓고 싶은데 조망이 좋고 조용한 동네면 좋겠어.'")}
                                        rows="4"
                                    />
                                </div>
                            </div>

                            {/* 검색 버튼 */}
                            <button 
                                className="ai_search_land_search_btn" 
                                onClick={handleSearch}
                                disabled={isLoading}
                            >
                                {isLoading ? t('검색 중...') : t('검색하기')}
                            </button>
                        </>
                    ) : (
                        <div className="ai_search_results_container">
                            {/* 검색 결과 리스트 */}
                            <h2 className="ai_search_land_results_title">{t('총 {n}건의 토지를 찾았어요!', { n: searchResults.length })}</h2>
                            <div className="ai_search_results_list">
                                {searchResults.map((result) => {
                                    const isOpen = expandedId === result.id;
                                    return (
                                        <div key={result.id} className="ai_search_result_item">
                                            <div className="ai_search_result_header">
                                                <div className='ai_search_result_header_wrap'>
                                                    <span className="ai_search_result_region">{t(result.region)}</span>
                                                    <span 
                                                        className="ai_search_result_address" 
                                                        onClick={() => {
                                                            const address = result.roadAddress || result.jibunAddress || result.address;
                                                            console.log('주소 클릭:', address);
                                                            // 주소 클릭 시 지도에서 검색
                                                            mapUtil.searchAddressToCoordinate(address);
                                                        }}
                                                        style={{ cursor: 'pointer', color: '#782DD9', textDecoration: 'underline' }}
                                                    >
                                                        {displayAddress(result.roadAddress || result.jibunAddress || result.address)}
                                                    </span>
                                                </div>
                                                <button
                                                    className="ai_search_result_detail_btn"
                                                    onClick={() => setExpandedId(isOpen ? null : result.id)}
                                                >
                                                    {isOpen ? t('접기') : t('상세 보기')}
                                                </button>
                                            </div>
                                            {isOpen && (
                                                <div className="ai_search_result_body">
                                                    <div className="ai_search_result_label">{t('AI 추천 이유')}</div>
                                                    <div className="ai_search_result_reason">
                                                        {result.reason}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {/* 다시하기 버튼 */}
                            <button className="ai_search_land_reset_btn" onClick={handleReset}>{t('다시하기')}</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}